import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://bbjyafcfcvdoqratavbz.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJianlhZmNmY3Zkb3FyYXRhdmJ6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ4NDI0MTAsImV4cCI6MjA3MDQxODQxMH0.rRCq_Xcw3fVPprZk3G4jyD0hxHm7w58_NasohLo9Z_A'
const supabaseServiceKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJianlhZmNmY3Zkb3FyYXRhdmJ6Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc1NDg0MjQxMCwiZXhwIjoyMDcwNDE4NDEwfQ.j9m4I4ifTpgIA8qrgjF_XCRXd5LcJVhCEkcAB-iDiqo'

// Create two clients - one for public operations (anon) and one for admin operations (service role)
export const supabase = createClient(supabaseUrl, supabaseAnonKey)
const supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey)

// Save PDF form to Supabase
export const savePDFForm = async (formData, pdfBlob, fileName) => {
  try {
    console.log('Starting PDF form save...', { fileName, blobSize: pdfBlob.size, blobType: pdfBlob.type })
    
    // Handle duplicate filenames by adding a counter
    let finalFileName = fileName
    let counter = 1
    
    while (true) {
      try {
        // Check if file already exists
        const { data: existingFile } = await supabaseAdmin.storage
          .from('pdfs')
          .list('', {
            limit: 1,
            search: finalFileName
          })
        
        if (existingFile && existingFile.length > 0) {
          // File exists, create new name with counter
          const nameWithoutExt = fileName.replace('.pdf', '')
          finalFileName = `${nameWithoutExt}_${counter}.pdf`
          counter++
        } else {
          // No conflict, break the loop
          break
        }
      } catch (error) {
        // If there's an error checking, assume no conflict and proceed
        break
      }
    }
    
    console.log('Final filename (after conflict resolution):', finalFileName)
    
    // Upload PDF file to storage using admin client to bypass storage RLS
    const { data: fileData, error: fileError } = await supabaseAdmin.storage
      .from('pdfs')
      .upload(finalFileName, pdfBlob, {
        contentType: 'application/pdf'
      })

    if (fileError) {
      console.error('File upload error:', fileError)
      throw fileError
    }
    
    console.log('File uploaded successfully:', fileData)

    // Get public URL for the file
    const { data: urlData } = supabaseAdmin.storage
      .from('pdfs')
      .getPublicUrl(finalFileName)
    
    console.log('Public URL generated:', urlData.publicUrl)

    // Prepare database data
    const dbData = {
      form_type: formData.formType,
      title: formData.title,
      location: formData.location,
      created_by: formData.createdBy,
      date: formData.date,
      staff_name: formData.staffName,
      form_data: formData.formData,
      comments: formData.comments,
      file_name: finalFileName,
      file_url: urlData.publicUrl,
      file_size: pdfBlob.size,
      created_at: new Date().toISOString()
    }
    
    console.log('Attempting database insert with data:', dbData)

    // Save form metadata to database using admin client to bypass RLS
    const { data, error } = await supabaseAdmin
      .from('pdf_forms')
      .insert([dbData])

    if (error) {
      console.error('Database insert error:', error)
      throw error
    }
    
    console.log('Database insert successful:', data)
    return data
  } catch (error) {
    console.error('Error saving PDF form:', error)
    throw error
  }
}

// Get all PDF forms - using anon client (respects RLS)
export const getAllPDFForms = async () => {
  try {
    const { data, error } = await supabase
      .from('pdf_forms')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching PDF forms:', error)
    return []
  }
}

// Get PDF forms count by type - using anon client (respects RLS)
export const getPDFFormsCountByType = async () => {
  try {
    const { data, error } = await supabase
      .from('pdf_forms')
      .select('form_type')

    if (error) throw error

    const counts = {}
    data.forEach(item => {
      counts[item.form_type] = (counts[item.form_type] || 0) + 1
    })

    return counts
  } catch (error) {
    console.error('Error counting PDF forms:', error)
    return {}
  }
}

// Delete PDF form - using admin client to bypass RLS
export const deletePDFForm = async (id, fileName) => {
  try {
    // Delete file from storage using admin client
    const { error: fileError } = await supabaseAdmin.storage
      .from('pdfs')
      .remove([fileName])

    if (fileError) throw fileError

    // Delete metadata from database using admin client
    const { error } = await supabaseAdmin
      .from('pdf_forms')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error deleting PDF form:', error)
    throw error
  }
}

// Download PDF - using anon client (respects RLS)
export const downloadPDF = async (pdfId) => {
  try {
    // Get PDF metadata
    const { data: pdf, error } = await supabase
      .from('pdf_forms')
      .select('*')
      .eq('id', pdfId)
      .single()

    if (error) throw error

    // Open PDF in new tab
    window.open(pdf.file_url, '_blank')
  } catch (error) {
    console.error('Error downloading PDF:', error)
    throw error
  }
}

// Get all forms - alias for getAllPDFForms for compatibility
export const getAllForms = async () => {
  return getAllPDFForms()
}

// Delete form - alias for deletePDFForm for compatibility
export const deleteForm = async (formId) => {
  try {
    // Get the form to find the filename
    const { data: form, error: fetchError } = await supabase
      .from('pdf_forms')
      .select('file_name')
      .eq('id', formId)
      .single()

    if (fetchError) throw fetchError

    // Delete using the filename
    return deletePDFForm(formId, form.file_name)
  } catch (error) {
    console.error('Error deleting form:', error)
    throw error
  }
}

// Save form data (for non-PDF forms) - using admin client to bypass RLS
export const saveForm = async (formData) => {
  try {
    const { data, error } = await supabaseAdmin
      .from('pdf_forms')
      .insert([
        {
          ...formData,
          created_at: new Date().toISOString()
        }
      ])

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error saving form:', error)
    throw error
  }
}

// Update form data - using admin client to bypass RLS
export const updateForm = async (formId, formData) => {
  try {
    const { data, error } = await supabaseAdmin
      .from('pdf_forms')
      .update({
        ...formData,
        updated_at: new Date().toISOString()
      })
      .eq('id', formId)

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error updating form:', error)
    throw error
  }
}
